import { z } from "zod";
import { scheduleApplicationPeriodStatusSchema } from "./schedule.schema";

export const scheduleSuspiciousDismissalBaseSchema = z.object({
    id: z.string().uuid(),
    workspaceId: z.string().uuid(),
    entityUuid: z.string().uuid(),
    transactionUuid: z.string().uuid(),
    dismissedByUserId: z.number().int(),
    dismissedAt: z.coerce.date(),
});

export type ScheduleSuspiciousDismissalBase = z.infer<
    typeof scheduleSuspiciousDismissalBaseSchema
>;

const entityIdSchema = z.number().int().positive();
/** Recognition review month, `yyyy-MM` with the month bounded to 01–12. */
export const REVENUE_RECOGNITION_PERIOD_KEY_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

export function isRevenueRecognitionPeriodKey(value: string): boolean {
    return REVENUE_RECOGNITION_PERIOD_KEY_PATTERN.test(value);
}

const periodKeySchema = z
    .string()
    .regex(REVENUE_RECOGNITION_PERIOD_KEY_PATTERN);

export const SCHEDULE_REVIEW_BULK_LIMIT = 500;
export const SCHEDULE_REVIEW_CONFIRM_DRAFTS_LIMIT = 100;
export const SCHEDULE_RECOGNITION_PERIOD_LIST_LIMIT = 500;

export const scheduleSuspiciousTransactionIdsBodySchema = z.object({
    entityId: entityIdSchema,
    transactionIds: z
        .array(z.number().int().positive())
        .min(1)
        .max(SCHEDULE_REVIEW_BULK_LIMIT),
});
export type ScheduleSuspiciousTransactionIdsBody = z.infer<
    typeof scheduleSuspiciousTransactionIdsBodySchema
>;

export const scheduleSuspiciousTransactionIdsResponseSchema = z.object({
    transactionIds: z.array(z.number().int()),
});

export const scheduleDraftApplicationIdsBodySchema = z.object({
    entityId: entityIdSchema,
    applicationIds: z
        .array(z.string().uuid())
        .min(1)
        .max(SCHEDULE_REVIEW_BULK_LIMIT),
});
export type ScheduleDraftApplicationIdsBody = z.infer<
    typeof scheduleDraftApplicationIdsBodySchema
>;

export const scheduleDraftApplicationIdsResponseSchema = z.object({
    applicationIds: z.array(z.string().uuid()),
});

export const scheduleReviewFailureSchema = z.object({
    applicationId: z.string().uuid(),
    message: z.string(),
});
export type ScheduleReviewFailure = z.infer<typeof scheduleReviewFailureSchema>;

export const confirmScheduleDraftsBodySchema = z.object({
    entityId: entityIdSchema,
    applicationIds: z
        .array(z.string().uuid())
        .min(1)
        .max(SCHEDULE_REVIEW_CONFIRM_DRAFTS_LIMIT),
});
export type ConfirmScheduleDraftsBody = z.infer<
    typeof confirmScheduleDraftsBodySchema
>;

export const confirmScheduleDraftsResponseSchema = z.object({
    confirmed: z.array(
        z.object({
            applicationId: z.string().uuid(),
            transactionId: z.number().int().nullable(),
        }),
    ),
    failed: z.array(scheduleReviewFailureSchema),
});
export type ConfirmScheduleDraftsResponse = z.infer<
    typeof confirmScheduleDraftsResponseSchema
>;

export const scheduleRecognitionReviewQuerySchema = z.object({
    entityId: z.coerce.number().int().positive(),
    periodKey: periodKeySchema,
});
export type ScheduleRecognitionReviewQuery = z.infer<
    typeof scheduleRecognitionReviewQuerySchema
>;

export const scheduleRecognitionPeriodSchema = z.object({
    id: z.string().uuid(),
    scheduleApplicationId: z.string().uuid(),
    scheduleTemplateId: z.string().uuid(),
    scheduleName: z.string(),
    sourceLabel: z.string(),
    periodKey: z.string(),
    effectiveDate: z.string(),
    amount: z.number(),
    status: scheduleApplicationPeriodStatusSchema,
    counterpartyName: z.string().nullable(),
    holdingAccountName: z.string(),
    recognitionAccountName: z.string(),
});
export type ScheduleRecognitionPeriod = z.infer<
    typeof scheduleRecognitionPeriodSchema
>;

export const listRecognitionReviewPeriodsResponseSchema = z.object({
    periodKey: periodKeySchema,
    postedCount: z.number().int().nonnegative(),
    periods: z
        .array(scheduleRecognitionPeriodSchema)
        .max(SCHEDULE_RECOGNITION_PERIOD_LIST_LIMIT),
});
export type ListRecognitionReviewPeriodsResponse = z.infer<
    typeof listRecognitionReviewPeriodsResponseSchema
>;

export const confirmRecognitionPeriodsBodySchema = z.object({
    entityId: entityIdSchema,
    periodKey: periodKeySchema,
    periodIds: z
        .array(z.string().uuid())
        .min(1)
        .max(SCHEDULE_REVIEW_BULK_LIMIT),
});
export type ConfirmRecognitionPeriodsBody = z.infer<
    typeof confirmRecognitionPeriodsBodySchema
>;

export const confirmAllRecognitionPeriodsBodySchema = z.object({
    entityId: entityIdSchema,
    periodKey: periodKeySchema,
});
export type ConfirmAllRecognitionPeriodsBody = z.infer<
    typeof confirmAllRecognitionPeriodsBodySchema
>;

export const confirmRecognitionPeriodsResponseSchema = z.object({
    postedPeriodIds: z.array(z.string().uuid()),
    skippedLockedPeriodIds: z.array(z.string().uuid()),
    failed: z.array(scheduleReviewFailureSchema),
});
export type ConfirmRecognitionPeriodsResponse = z.infer<
    typeof confirmRecognitionPeriodsResponseSchema
>;
